import { createContext, useCallback, useEffect, useRef, useState } from "react";
import jwtDecode from "jwt-decode";
import axios from "axios";

import { APP_API_BASE } from "../config/api";
export const AuthContext = createContext(null);

const FIREBASE_REFRESH_URL = "https://securetoken.googleapis.com/v1/token";

const API_BASE = `${APP_API_BASE}/api/v1/users`;
const AUTH_API_BASE = `${APP_API_BASE}/api/v1/auth`;

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const [user, setUser] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [activeOrg, setActiveOrg] = useState(null);
  const [permissions, setPermissions] = useState(new Set());
  const logoutInProgressRef = useRef(false);

  // ---------- Token helpers ----------

  const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token);
      return decoded.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  };

  const refreshIdToken = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) throw new Error("No refresh token");

    const response = await axios.post(
      FIREBASE_REFRESH_URL,
      new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
      }),
      {
        params: {
          key: process.env.REACT_APP_FIREBASE_API_KEY,
        },
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      },
    );

    const { id_token, refresh_token } = response.data;

    localStorage.setItem("authToken", id_token);
    localStorage.setItem("refreshToken", refresh_token);

    return id_token;
  };

  // ---------- API helpers ----------

  const authHeaders = (token, orgId = null) => ({
    Authorization: `Bearer ${token}`,
    ...(orgId ? { "X-Org-ID": orgId } : {}),
  });

  // ---------- Load user + orgs (NO permissions) ----------

  const loadUser = async (token) => {
    const res = await axios.get(`${API_BASE}/me`, {
      headers: authHeaders(token),
    });

    const { user: currentUser, organizations } = res.data;

    setUser(currentUser);
    setOrganizations(organizations);

    if (!organizations.length) {
      setActiveOrg(null);
      setPermissions(new Set());
      return { org: null, user: currentUser };
    }

    const savedOrgId = localStorage.getItem("activeOrgId");
    const resolvedOrg =
      organizations.find((o) => o.id === savedOrgId) || organizations[0];

    setActiveOrg(resolvedOrg);
    localStorage.setItem("activeOrgId", resolvedOrg.id);

    return { org: resolvedOrg, user: currentUser };
  };

  // ---------- Load permissions for active org ----------

  const loadPermissions = async (token, orgId) => {
    const res = await axios.get(`${API_BASE}/me/context`, {
      headers: authHeaders(token, orgId),
    });

    setPermissions(new Set(res.data.permissions || []));
  };

  const loadPlatformPermissions = async (token) => {
    const res = await axios.get(`${API_BASE}/me/platform-context`, {
      headers: authHeaders(token),
    });

    setPermissions(new Set(res.data.permissions || []));
  };

  // ---------- App bootstrap ----------

  useEffect(() => {
    const initAuth = async () => {
      try {
        let token = localStorage.getItem("authToken");
        if (!token) return;

        if (isTokenExpired(token)) {
          token = await refreshIdToken();
        }

        const { org, user: currentUser } = await loadUser(token);
        if (org) {
          await loadPermissions(token, org.id);
        } else if (currentUser?.global_role === "superadmin") {
          await loadPlatformPermissions(token);
        }

        setIsAuthenticated(true);
      } catch (err) {
        console.error("Auth init failed:", err);
        logout();
      } finally {
        setAuthLoading(false);
      }
    };

    initAuth();
  }, []);

  // ---------- Public API ----------

  const login = async ({ idToken, refreshToken }) => {
    localStorage.setItem("authToken", idToken);
    localStorage.setItem("refreshToken", refreshToken);

    await axios.post(
      `${AUTH_API_BASE}/session/login`,
      {},
      {
        headers: authHeaders(idToken),
      },
    );

    const { org, user: currentUser } = await loadUser(idToken);
    if (org) {
      await loadPermissions(idToken, org.id);
    } else if (currentUser?.global_role === "superadmin") {
      await loadPlatformPermissions(idToken);
    }

    setIsAuthenticated(true);
    logoutInProgressRef.current = false;
  };

  const switchOrg = async (org) => {
    const token = localStorage.getItem("authToken");
    if (!token) return;

    setActiveOrg(org);
    localStorage.setItem("activeOrgId", org.id);

    await loadPermissions(token, org.id);
  };

  const logout = useCallback(async (reason = null) => {
    const token = localStorage.getItem("authToken");

    if (token && reason !== "session_expired") {
      try {
        await axios.post(
          `${AUTH_API_BASE}/logout`,
          {},
          {
            headers: authHeaders(token),
          },
        );
      } catch (err) {
        console.error("Logout request failed:", err);
      }
    }

    if (reason === "session_expired") {
      sessionStorage.setItem(
        "authLogoutReason",
        "Your session expired. Please sign in again.",
      );
    }
    localStorage.clear();
    setIsAuthenticated(false);
    setUser(null);
    setOrganizations([]);
    setActiveOrg(null);
    setPermissions(new Set());
    logoutInProgressRef.current = false;
  }, []);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error?.response?.status === 401) {
          const requestUrl = error.config?.url || "";
          const isFirebaseCall =
            requestUrl.includes("securetoken.googleapis.com") ||
            requestUrl.includes("identitytoolkit.googleapis.com");

          if (!isFirebaseCall && !logoutInProgressRef.current) {
            logoutInProgressRef.current = true;
            logout("session_expired");
            if (window.location.pathname !== "/login") {
              window.location.replace("/login");
            }
          }
        }

        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        authLoading,
        user,
        organizations,
        activeOrg,
        permissions,
        login,
        logout,
        switchOrg,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
